import dayjs from "dayjs";
import { hasNullRequiredField } from "../../shared/helpers/objectPath";
import { mergeColumnsWithCurrentState } from "../../shared/helpers/tableColumns";

type EditedRow = {
  index: string | number;
  record?: any;
  touchedFields?: Record<string, boolean>;
  [key: string]: any;
};

export { hasNullRequiredField };

const ROUTE_ROAD_TYPE_LABEL_TO_VALUE: Record<string, string> = {
  "BẮC - NAM": "ROAD",
  CẢNG: "EXIM",
  BIÊN: "BORDER",
  "ĐƯỜNG THUỶ": "WATERWAY",
  "ĐƯỜNG THỦY": "WATERWAY",
  "CHUYỂN KHO": "TRANSFER",
};

const ROUTE_ROAD_TYPE_VALUES = new Set([
  "ROAD",
  "EXIM",
  "BORDER",
  "WATERWAY",
  "TRANSFER",
]);

export const normalizeRouteRoadType = (roadType: any) => {
  const rawValue = roadType?.value ?? roadType?.code ?? roadType;
  if (typeof rawValue !== "string") return rawValue;

  const normalizedValue = rawValue.trim().toUpperCase();
  if (ROUTE_ROAD_TYPE_VALUES.has(normalizedValue)) {
    return normalizedValue;
  }

  return ROUTE_ROAD_TYPE_LABEL_TO_VALUE[normalizedValue] ?? rawValue;
};

export const buildRouteBasePayload = (rowEdit: any) => ({
  code: rowEdit.code,
  receipt: { wId: rowEdit.receipt?.wId ?? rowEdit.receipt?.value },
  delivery: { wId: rowEdit.delivery?.wId ?? rowEdit.delivery?.value },
  roadType: normalizeRouteRoadType(rowEdit.roadType),
  description: rowEdit.description,
  haveGoodsDistance: rowEdit.haveGoodsDistance,
  noGoodsDistance: rowEdit.noGoodsDistance,
  region: rowEdit.region?._id ?? rowEdit.region,
  direction: rowEdit.direction?.code ?? rowEdit.direction,
});

export const buildRouteSalaryPayload = (rowEdit: any) => ({
  cont20: rowEdit.cont20,
  cont40: rowEdit.cont40,
  cont20Combined1: rowEdit.cont20Combined1,
  cont20Combined2: rowEdit.cont20Combined2,
  cont40Combined1: rowEdit.cont40Combined1,
  cont40Combined2: rowEdit.cont40Combined2,
});

export const buildRouteVersionPayload = (rowEdit: any) => ({
  name: rowEdit.name,
  note: rowEdit.note,
  validFrom:
    typeof rowEdit.validFrom === "string"
      ? rowEdit.validFrom
      : rowEdit.validFrom?.format("YYYY-MM-DD"),
});

export const buildRouteImportConfirmPayload = (
  payload: {
    fileName: string;
    versionId: string;
  },
  formValues: {
    name?: string;
    validFrom?: any;
  },
  versions: Array<{ _id?: string; validFrom?: string | null }>,
) => {
  if (formValues.name && formValues.name !== "new") {
    const selectedVersion = versions.find(
      (item) => item._id === formValues.name,
    );

    return {
      ...payload,
      versionId: formValues.name,
      validFrom:
        selectedVersion?.validFrom ??
        (formValues.validFrom
          ? new Date(formValues.validFrom).toISOString()
          : undefined),
    };
  }

  return {
    ...payload,
    versionId: payload.versionId,
    validFrom: formValues.validFrom
      ? new Date(formValues.validFrom).toISOString()
      : undefined,
  };
};

export const getRouteImportValidFrom = (
  versionId: string,
  versions: Array<{ _id?: string; validFrom?: string | null }>,
) => {
  if (!versionId || versionId === "new") {
    return null;
  }

  const selectedVersion = versions.find((item) => item._id === versionId);
  return selectedVersion?.validFrom ? dayjs(selectedVersion.validFrom) : null;
};

export const toRouteDetailFormValues = (record: any) => ({
  ...record,
  receipt: {
    value: record.receipt?.wId ? record.receipt.wId : record.receipt?.value,
    key: record.receipt?.wId ? record.receipt.wId : record.receipt?.value,
    label: record.receipt?.address,
  },
  delivery: {
    value: record.delivery?.wId ? record.delivery.wId : record.delivery?.value,
    key: record.delivery?.wId ? record.delivery.wId : record.delivery?.value,
    label: record.delivery?.address,
  },
  region: record.region?._id ?? record.region,
  roadType: normalizeRouteRoadType(record.roadType),
});

export const resolveRouteDetailRecord = (record: any, activeTab?: string) =>
  activeTab === "2" && record?.routerId
    ? { ...record, _id: record.routerId }
    : record;

export const updateEditedRows = (
  rows: EditedRow[],
  key: string,
  value: any,
  index: string | number,
) => {
  const next = [...rows];
  const rowIndex = next.findIndex((item) => item.index === index);
  if (rowIndex === -1) return rows;

  const row = { ...next[rowIndex] };
  const record = { ...(row.record || {}) };

  record[key] = value;
  row.touchedFields = { ...(row.touchedFields || {}), [key]: true };
  row.record = record;
  next[rowIndex] = row;

  return next;
};

export const removeEditedRouteRow = (
  rows: EditedRow[],
  index: string | number,
) => rows.filter((item) => item.index !== index);

export const replaceRouteRowAfterSave = (
  rows: any[],
  index: string | number,
  savedRow: any,
) => rows.map((row) => (row._id === index ? savedRow : row));

export const formatRouteVersionDate = (value: any) => {
  if (!value) return value;
  return typeof value === "string" ? value : dayjs(value).format("YYYY-MM-DD");
};

export const canCreateRouteRow = (
  activeTab?: string,
  activeRowKey?: string | null,
) => activeTab !== "2" || Boolean(activeRowKey);

export const shouldLoadRouteSalaryRows = (
  activeTab?: string,
  activeRowKey?: string | null,
) => activeTab === "2" && Boolean(activeRowKey);

export const canDeleteRouteRow = (activeTab?: string) => activeTab !== "2";

export const resolveRouteVersionRowKey = (
  rows: Array<{ _id?: string | null }>,
  currentActiveRowKey?: string | null,
) => {
  if (!rows.length) return null;

  return rows.some((row) => row?._id === currentActiveRowKey)
    ? (currentActiveRowKey ?? null)
    : (rows[0]?._id ?? null);
};

export const getRouteVersionActionDisabledState = (
  checkPermissionsFn: (
    screen: string,
    action: string,
    features: any,
  ) => boolean,
  features: any,
) => ({
  edit: !checkPermissionsFn("FIXED_ROUTE_SALARY", "edit", features),
  download: !checkPermissionsFn("FIXED_ROUTE_SALARY", "export", features),
});

export const resolveRouteSaveOutcome = ({
  activeTab,
  isNew,
  rowEdit,
  response,
}: {
  activeTab?: string;
  isNew: boolean;
  rowEdit: any;
  response?: any;
}) => {
  if (activeTab === "2" && isNew) {
    return {
      nextRow: null,
      shouldReload: true,
    };
  }

  if (activeTab === "1" && isNew) {
    const nextRow = { ...rowEdit };
    delete nextRow.status;
    nextRow._id = response?.data?._id;

    return {
      nextRow,
      shouldReload: false,
    };
  }

  return {
    nextRow: rowEdit,
    shouldReload: false,
  };
};

export const applyRouteDetailToSalaryRow = ({
  editRows,
  rows,
  targetId,
  detail,
}: {
  editRows: any[];
  rows: any[];
  targetId: string;
  detail: any;
}) => {
  const nextEditRows = editRows.map((item: any) =>
    item.index === targetId
      ? {
          ...item,
          record: {
            ...item.record,
            ...detail,
            _id: targetId,
            routerId: detail._id,
          },
        }
      : item,
  );

  const nextRows = rows.map((item: any) =>
    item._id === targetId
      ? { ...item, ...detail, routerId: detail._id, _id: targetId }
      : item,
  );

  return {
    nextEditRows,
    nextRows,
  };
};

export const getRouteTabChangeState = (pageSize: number) => ({
  selectedRowKeys: [],
  editIndex: [],
  editIndexVersion: [],
  filters: {},
  pagination: {
    current: 1,
    pageSize,
  },
});

export const applyRouteDetailUpdateToRows = ({
  rows,
  routeId,
  values,
}: {
  rows: any[];
  routeId: string;
  values: any;
}) =>
  rows.map((row: any) => {
    if (row._id === routeId) {
      return {
        ...row,
        ...values,
      };
    }

    if (row.routerId === routeId) {
      return {
        ...row,
        ...values,
        _id: row._id,
        routerId: row.routerId,
      };
    }

    return row;
  });

export const applyRouteDeleteResult = ({
  rows,
  deletedIds,
  total,
}: {
  rows: any[];
  deletedIds: string[];
  total: number;
}) => {
  const nextRows = rows.filter((row: any) => !deletedIds.includes(row._id));

  return {
    nextRows,
    nextTotal: Math.max(total - deletedIds.length, 0),
  };
};

export const getRoutePaginationAfterDelete = ({
  total,
  current,
  pageSize,
  deletedCount,
}: {
  total: number;
  current: number;
  pageSize: number;
  deletedCount: number;
}) => {
  const nextTotal = Math.max(total - deletedCount, 0);
  const maxPage = Math.max(Math.ceil(nextTotal / pageSize), 1);

  return {
    total: nextTotal,
    current: Math.min(current, maxPage),
    pageSize,
  };
};

export const getRouteVersionSelectionState = (pageSize: number) => ({
  editIndex: [],
  filters: {},
  pagination: {
    current: 1,
    pageSize,
  },
});

export const resolveRouteImportSuccessActiveRowKey = ({
  selectedVersionId,
  createdVersionId,
}: {
  selectedVersionId?: string | null;
  createdVersionId?: string | null;
}) => {
  if (selectedVersionId && selectedVersionId !== "new") {
    return selectedVersionId;
  }

  return createdVersionId ?? null;
};

export const mergeRouteColumnsWithCurrentState = mergeColumnsWithCurrentState;
